/**
 * Thin Sentry init wrapper.
 *
 * Activates only when REACT_APP_SENTRY_DSN is set. Otherwise every
 * function is a no-op so the bundle stays tiny in development and
 * activates at launch by simply adding the DSN env var.
 *
 * Uses dynamic import so we never pull the Sentry SDK into the main
 * bundle if it isn't configured.
 *
 * **Sentry ↔ PostHog session replay link**: a `beforeSend` hook
 * reads PostHog's current session_id and attaches the replay URL
 * as a Sentry tag + extra. Result: every Sentry issue links to the
 * exact PostHog session replay of the user who hit it — you watch
 * the bug instead of guessing from the stack trace.
 */
let _client = null;
let _ready = false;

/**
 * Build the PostHog session replay URL for the current PostHog session.
 * Returns null when PostHog isn't booted yet (no consent / no key).
 */
function _posthogReplayUrl() {
  try {
    const ph = typeof window !== "undefined" ? window.posthog : null;
    if (!ph || typeof ph.get_session_id !== "function") return null;
    const sid = ph.get_session_id();
    if (!sid) return null;
    const host = (process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com")
      .replace("us.i.posthog.com", "us.posthog.com")
      .replace("eu.i.posthog.com", "eu.posthog.com");
    // Anchor at session timestamp so the replay seeks straight to the
    // point of failure — no scrubbing through 20 minutes of usage.
    const ts = Date.now();
    return `${host}/replay/${sid}?t=${Math.floor(ts / 1000)}`;
  } catch {
    return null;
  }
}

export async function initSentry() {
  if (_ready) return;
  _ready = true;
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return; // no-op until DSN provided
  try {
    const Sentry = await import("@sentry/react");
    // Environment from the ACTUAL hostname, not env vars — the same
    // .env ships to both preview and production deploys, so
    // REACT_APP_SENTRY_ENV would tag production events "preview".
    // Hostname never lies (same self-healing pattern as the
    // password-reset link fix).
    const host = window.location.hostname;
    const environment = host.includes("preview.emergentagent")
      ? "preview"
      : host === "localhost" || host === "127.0.0.1"
        ? "development"
        : "production";
    Sentry.init({
      dsn,
      environment,
      release: process.env.REACT_APP_VERSION || undefined,
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 0.1,
      // Suppress browser-extension noise. Password managers (1Password,
      // Dashlane, LastPass) and assorted dev-tools extensions inject
      // their own hooks into the page and occasionally throw inside
      // them. These errors are not in our code, we can't fix them, and
      // they swamp the Sentry inbox.
      ignoreErrors: [
        // 1Password / Dashlane keyboard-input hook
        /enableDidUserTypeOnKeyboardLogging/i,
        // Generic extension-source markers
        /chrome-extension:\/\//i,
        /moz-extension:\/\//i,
        /safari-extension:\/\//i,
        // Common no-op browser errors
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        // Network-layer noise our axios layer already handled
        "Non-Error promise rejection captured",
      ],
      // Belt-and-suspenders: also reject events whose stack trace
      // originates inside a browser extension URL.
      denyUrls: [
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-extension:\/\//i,
      ],
      // Bridge to PostHog session replay — runs on every event.
      beforeSend(event, hint) {
        // ── Drop transient network/gateway noise ─────────────────
        // 502 / 503 / 504 / pure network errors during deploys, cold
        // starts, or mobile-Safari connection drops are infrastructure,
        // not code bugs. Our axios layer already retries them once
        // (see lib/api.js); only the rare double-failure should ever
        // reach Sentry, and even then it's not actionable for the
        // engineering team. Without this filter, a single 30-second
        // backend restart spawns ~20 occurrences per page-load (every
        // dashboard fan-out request fails simultaneously).
        try {
          const ex = hint?.originalException;
          const status = ex?.response?.status;
          const isAxios = ex?.isAxiosError || ex?.name === "AxiosError";
          const isTransientStatus = status === 502 || status === 503 || status === 504
            || status === 520 || status === 521 || status === 522
            || status === 523 || status === 524;
          // 401s on background polls (notifications, messages,
          // /me/...) are session-expired errors, not bugs. The axios
          // interceptor already redirects the user to /login when it
          // matters; Sentry doesn't need to hear about every poll
          // that catches a stale JWT.
          const is401 = status === 401;
          // Backend-emitted "feature not configured on this deployment"
          // 503s (Apple Sign In keys missing, FCM keys missing, etc.)
          // are deliberate `HTTPException`s, not crashes. They mean
          // "wire up this integration in prod env vars" — a runbook
          // item, not a code bug.
          const notConfigured = /not (yet )?configured on this deployment/i.test(
            ex?.response?.data?.detail || ex?.message || ""
          );
          const isNetworkErr = isAxios && !ex?.response && /Network Error|timeout/i.test(ex?.message || "");
          // Drop if it was already retried by our axios interceptor —
          // we don't need to hear about the same gateway hiccup twice.
          if (ex?.__retriedTransient) return null;
          if (isAxios && (isTransientStatus || isNetworkErr || is401 || notConfigured)) return null;
        } catch {
          /* never let the filter crash sending real errors */
        }

        const url = _posthogReplayUrl();
        if (url) {
          event.tags = { ...(event.tags || {}), posthog_session: "linked" };
          event.contexts = {
            ...(event.contexts || {}),
            posthog: { session_replay_url: url },
          };
          // Also append as a clickable extra in case the contexts pane
          // is collapsed in the Sentry UI.
          event.extra = {
            ...(event.extra || {}),
            "PostHog session replay": url,
          };
        }
        return event;
      },
    });
    _client = Sentry;
  } catch (e) {
    // SDK not installed — that's expected when DSN is blank.
    // eslint-disable-next-line no-console
    console.warn("[sentry] SDK not installed; install @sentry/react to enable.", e?.message || e);
  }
}

export function captureException(err, ctx = {}) {
  try {
    if (_client?.captureException) {
      const replayUrl = _posthogReplayUrl();
      _client.captureException(err, {
        extra: { ...ctx, ...(replayUrl ? { "PostHog session replay": replayUrl } : {}) },
      });
    } else {
      // eslint-disable-next-line no-console
      console.error("[capture]", err, ctx);
    }
  } catch {
    /* swallow — never let the error reporter crash */
  }
}

export function setUser(user) {
  try {
    if (_client?.setUser) {
      _client.setUser(user ? { id: user.id, email: user.email } : null);
    }
  } catch {
    /* noop */
  }
}
